/**
 * fbxCharacterToGlb — an FBX CHARACTER (a Mixamo download, a DCC export)
 * becomes a GLB before anything else reads it. One conversion, after which
 * the body walks the proven GLB door: the facing turn, the rig, the hands,
 * the export World receives. FBX knowledge stays at the Load stage (ADR-008 §2).
 *
 * Four repairs, each measured on the Mixamo corpus (2026-10-04):
 *   1. WELD — FBXLoader builds a separate copy of the armature per skinned mesh
 *      (Claire: 4 meshes, 348 bones = 4 × 87, none shared), which the rig
 *      reader rightly calls four skeletons. Copies are welded onto ONE armature
 *      by bone name, refused if a copy does not stand where its original does.
 *   2. UNITS — FBX is centimetres (UnitScaleFactor 1 → 0.01 m/unit); a glTF
 *      body is metres. Baked into positions, vertices and binds, never left as
 *      a root scale the IBMs would disagree with.
 *   3. MATERIALS — Phong/Lambert become metal-free PBR (glTF has no Phong).
 *   4. TEXTURES — embedded images decode asynchronously; the conversion waits
 *      for them. One that never arrives (a path to the author's own disk) is
 *      DROPPED AND REPORTED — never a silent grey body.
 * The file's animation takes are not carried: this door makes the body.
 * Depends on: three (FBXLoader, GLTFExporter), load-fbx. Depended on by: app run + load door.
 */
import {
  Bone,
  Camera,
  Light,
  LoadingManager,
  Matrix4,
  Mesh,
  MeshLambertMaterial,
  MeshPhongMaterial,
  MeshStandardMaterial,
  Skeleton,
  SkinnedMesh,
  Texture,
  Vector3,
  type BufferGeometry,
  type Material,
  type Object3D,
} from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { GLTFExporter } from 'three/addons/exporters/GLTFExporter.js';
import { AdapterError } from './errors.js';
import { installFileReaderShim } from './glb-internals.js';
import { classifyTake, loadFBX } from './load-fbx.js';

/** What the conversion did — every liberty named, for the status line and the console. */
export interface FbxCharacterReport {
  readonly skinnedMeshes: number;
  /** Duplicate bone copies welded onto the one kept per name (0 when the file already shared one armature). */
  readonly bonesWelded: number;
  /** Skins bound at another pose than the body, reposed into the body's bind (one bind per bone). */
  readonly skinsReposed: readonly string[];
  readonly metersPerUnit: number;
  /** The body's rest height in metres, as authored (Mixamo keeps the uploader's size: 1 cm and 3.7 m both ship). */
  readonly heightMeters: number;
  readonly materialsConverted: number;
  /** `material.slot` for every texture that never decoded and was dropped. */
  readonly texturesDropped: readonly string[];
  /** Takes in the file that this door did not carry (it makes the body, not the clip). */
  readonly takesNotCarried: readonly string[];
}

export interface FbxCharacterGlb {
  readonly glb: ArrayBuffer;
  readonly report: FbxCharacterReport;
}

/** The FBX is not a character this door can make into a body. */
export class FbxCharacterRefusal extends AdapterError {
  constructor(readonly label: string, readonly reason: string) {
    super(`"${label}" cannot open as a character — ${reason}`);
    this.name = 'FbxCharacterRefusal';
  }
}

/** What an FBX is FOR, read without decoding an image: a body (skins) and/or a clip source (animation takes). */
export function fbxShape(bytes: ArrayBuffer): { readonly hasSkin: boolean; readonly animationTakes: number } {
  let hasSkin = false;
  const scene = new FBXLoader(textureFreeManager()).parse(bytes, '');
  scene.traverse((o) => {
    if (o instanceof SkinnedMesh) hasSkin = true;
  });
  return { hasSkin, animationTakes: scene.animations.filter((c) => classifyTake(c) === 'animation').length };
}

/** A position that agrees within this many file units is the same joint (0.1 mm at cm). */
const WELD_TOLERANCE_UNITS = 0.01;
const TEXTURE_WAIT_MS = 30_000;
/** Outside this, the status line says the body's authored height out loud. */
const PLAUSIBLE_HEIGHT_M: readonly [number, number] = [0.5, 3];

/** Bind-pose height of every skin together, in scene units (metres once the units are baked). */
function restHeight(meshes: readonly SkinnedMesh[]): number {
  let lo = Infinity;
  let hi = -Infinity;
  const v = new Vector3();
  for (const m of meshes) {
    const position = m.geometry.getAttribute('position');
    for (let i = 0; i < position.count; i += 1) {
      v.fromBufferAttribute(position, i).applyMatrix4(m.bindMatrix);
      lo = Math.min(lo, v.y);
      hi = Math.max(hi, v.y);
    }
  }
  return hi > lo ? hi - lo : 0;
}

const cache = new WeakMap<ArrayBuffer, Promise<FbxCharacterGlb>>();
const settled = new WeakMap<ArrayBuffer, FbxCharacterGlb>();

/**
 * Convert once per buffer: a Document re-runs on every edit, and the same
 * bytes must not be re-decoded each time. Keyed by the buffer itself.
 */
export function fbxCharacterToGlb(bytes: ArrayBuffer, label: string): Promise<FbxCharacterGlb> {
  let pending = cache.get(bytes);
  if (pending === undefined) {
    pending = convert(bytes, label).then((out) => {
      settled.set(bytes, out);
      return out;
    });
    pending.catch(() => cache.delete(bytes));
    cache.set(bytes, pending);
  }
  return pending;
}

/** The finished conversion of these bytes, if one has completed — for synchronous readers (the World check). */
export function convertedFbxCharacter(bytes: ArrayBuffer): FbxCharacterGlb | undefined {
  return settled.get(bytes);
}

async function convert(bytes: ArrayBuffer, label: string): Promise<FbxCharacterGlb> {
  const textures = trackingManager();
  // Headless (no DOM) an embedded image cannot decode at all; parse without
  // them and let step 4 report every map as dropped.
  const manager = typeof document === 'undefined' ? textureFreeManager() : textures.manager;
  const loaded = loadFBX(bytes, new FBXLoader(manager));
  const scene = loaded.scene;
  const meshes: SkinnedMesh[] = [];
  scene.traverse((o) => {
    if (o instanceof SkinnedMesh) meshes.push(o);
  });
  if (meshes.length === 0) {
    throw new FbxCharacterRefusal(label, 'it carries no skinned mesh (an animation-only file? use Add animation…)');
  }
  const bonesWelded = weldArmatures(scene, meshes, label);
  const skinsReposed = unifyBinds(meshes);
  bakeUnitScale(scene, loaded.convention.metersPerUnit);
  if (typeof document !== 'undefined') await textures.done();
  const materialsConverted = convertMaterials(scene);
  const heightMeters = restHeight(meshes);
  const texturesDropped = dropUndecodedTextures(scene);
  scene.animations = [];
  // A DCC export can carry its cameras and lights; a body file carries neither.
  const extras: Object3D[] = [];
  scene.traverse((o) => {
    if (o instanceof Camera || o instanceof Light) extras.push(o);
  });
  for (const o of extras) o.removeFromParent();
  const uninstallShim = installFileReaderShim();
  let glb: ArrayBuffer;
  try {
    // `trs: true`, as exportGlb writes: a matrix node decomposes back onto the other sign of a quaternion.
    const out = await new GLTFExporter().parseAsync(scene, { binary: true, trs: true, animations: [] });
    if (!(out instanceof ArrayBuffer)) throw new FbxCharacterRefusal(label, 'the GLB writer returned no binary');
    glb = out;
  } finally {
    uninstallShim?.();
  }
  return {
    glb,
    report: {
      skinnedMeshes: meshes.length,
      bonesWelded,
      skinsReposed,
      metersPerUnit: loaded.convention.metersPerUnit,
      heightMeters,
      materialsConverted,
      texturesDropped,
      takesNotCarried: loaded.takes.filter((t) => t.classification === 'animation').map((t) => t.name),
    },
  };
}

/** One line for the status bar: what the door changed, what it could not keep. */
export function fbxCharacterReportLine(report: FbxCharacterReport): string {
  const parts = [`converted from FBX (${report.skinnedMeshes} mesh${report.skinnedMeshes === 1 ? '' : 'es'}`];
  if (report.metersPerUnit !== 1) parts.push(`${report.metersPerUnit === 0.01 ? 'cm' : `${report.metersPerUnit} m/unit`} → m`);
  parts[parts.length - 1] += ')';
  if (report.heightMeters < PLAUSIBLE_HEIGHT_M[0] || report.heightMeters > PLAUSIBLE_HEIGHT_M[1]) {
    parts.push(`it stands ${report.heightMeters < 0.1 ? report.heightMeters.toFixed(3) : report.heightMeters.toFixed(2)} m tall as authored — check its size`);
  }
  if (report.texturesDropped.length > 0) {
    parts.push(`${report.texturesDropped.length} texture(s) were not inside the file and were dropped: ${report.texturesDropped.slice(0, 4).join(', ')}${report.texturesDropped.length > 4 ? '…' : ''}`);
  }
  if (report.takesNotCarried.length > 0) parts.push(`its animation "${report.takesNotCarried[0]}" was not kept — add it with Add animation…`);
  return parts.join(' · ');
}

// ── 1. weld ────────────────────────────────────────────────────────────────

/** The body leads: most bones, then most vertices (Erika's four skins tie at 67 bones; her eyes must not set the bind). */
function bodyFirst(meshes: readonly SkinnedMesh[]): SkinnedMesh[] {
  const verts = (m: SkinnedMesh): number => m.geometry.getAttribute('position')?.count ?? 0;
  return [...meshes].sort((x, y) => y.skeleton.bones.length - x.skeleton.bones.length || verts(y) - verts(x));
}

/**
 * ONE bone per name. FBXLoader builds a bone per skin cluster and NESTS the
 * copies (Claire: each mesh's Hips is the child of another mesh's Hips, all in
 * one hierarchy), so the rig reader sees as many skeletons as meshes. The
 * kept bone for a name is the one the body's skin binds (else the shallowest);
 * it hangs from the kept bone of its nearest DIFFERENTLY-named ancestor, world
 * transform preserved; every skin rebinds to the kept bones with its own
 * inverse binds; the copies go. A copy standing elsewhere than its original
 * is refused — one body cannot have two of a joint. Returns the copies removed.
 */
export function weldArmatures(scene: Object3D, meshes: readonly SkinnedMesh[], label: string): number {
  scene.updateMatrixWorld(true);
  const all: Bone[] = [];
  scene.traverse((o) => {
    if (o instanceof Bone) all.push(o);
  });
  const kept = new Map<string, Bone>();
  for (const m of bodyFirst(meshes)) {
    for (const b of m.skeleton.bones) if (!kept.has(b.name)) kept.set(b.name, b);
  }
  for (const b of all) if (!kept.has(b.name)) kept.set(b.name, b); // traversal is pre-order: shallowest first
  const keptSet = new Set(kept.values());
  const copies = all.filter((b) => !keptSet.has(b));
  if (copies.length === 0) return 0;

  const at = new Vector3();
  const home = new Vector3();
  for (const b of copies) {
    at.setFromMatrixPosition(b.matrixWorld);
    home.setFromMatrixPosition(kept.get(b.name)!.matrixWorld);
    if (at.distanceTo(home) > WELD_TOLERANCE_UNITS) {
      throw new FbxCharacterRefusal(
        label,
        `two copies of bone "${b.name}" stand ${at.distanceTo(home).toFixed(3)} units apart — one body cannot have two`,
      );
    }
  }

  const depth = (o: Object3D): number => {
    let d = 0;
    for (let p = o.parent; p !== null; p = p.parent) d += 1;
    return d;
  };
  const parentFor = new Map<Bone, Object3D>();
  for (const b of keptSet) {
    let p = b.parent;
    while (p instanceof Bone && p.name === b.name) p = p.parent;
    if (p === null) throw new FbxCharacterRefusal(label, `bone "${b.name}" is outside the scene`);
    parentFor.set(b, p instanceof Bone ? kept.get(p.name)! : p);
  }
  for (const b of [...keptSet].sort((x, y) => depth(x) - depth(y))) {
    const p = parentFor.get(b)!;
    if (b.parent !== p) p.attach(b);
  }
  for (const b of copies) {
    let holdsMesh = false;
    b.traverse((o) => {
      if (o instanceof Mesh) holdsMesh = true;
    });
    if (holdsMesh) throw new FbxCharacterRefusal(label, `a copy of bone "${b.name}" carries a mesh of its own`);
    b.removeFromParent();
  }
  for (const mesh of meshes) {
    const bones = mesh.skeleton.bones.map((b) => kept.get(b.name)!);
    mesh.bind(new Skeleton(bones, mesh.skeleton.boneInverses.map((m) => m.clone())), mesh.bindMatrix.clone());
  }
  scene.updateMatrixWorld(true);
  return copies.length;
}

/**
 * One bind per bone. A DCC can bind each mesh at a slightly different pose
 * (Erika's lashes 0.5° off her body at the head, EXO's brows 1.0° at the
 * forearm), and one skeleton cannot carry two binds for a joint. The body's
 * skin (bodyFirst) owns a bone's inverse bind; every other skin is skinned
 * INTO that pose (linear blend, the way it would render there) and adopts it.
 * Returns the skins reposed.
 */
export function unifyBinds(meshes: readonly SkinnedMesh[]): string[] {
  const owner = new Map<Bone, Matrix4>();
  for (const m of bodyFirst(meshes)) {
    m.skeleton.bones.forEach((b, i) => {
      if (!owner.has(b)) owner.set(b, m.skeleton.boneInverses[i]!);
    });
  }
  const reposed: string[] = [];
  for (const mesh of meshes) {
    const { bones, boneInverses } = mesh.skeleton;
    const differs = bones.some((b, i) => !matricesAgree(owner.get(b)!, boneInverses[i]!));
    if (!differs) continue;
    // Per bone: mesh-local bind → world → the bone at ITS bind → world at the OWNER's bind → mesh-local.
    const perBone = bones.map((b, i) =>
      new Matrix4()
        .copy(mesh.bindMatrixInverse)
        .multiply(new Matrix4().copy(owner.get(b)!).invert())
        .multiply(boneInverses[i]!)
        .multiply(mesh.bindMatrix),
    );
    repose(mesh.geometry as BufferGeometry, perBone);
    mesh.bind(new Skeleton([...bones], bones.map((b) => owner.get(b)!.clone())), mesh.bindMatrix.clone());
    reposed.push(mesh.name || '(unnamed)');
  }
  return reposed;
}

function matricesAgree(a: Matrix4, b: Matrix4): boolean {
  for (let k = 0; k < 16; k += 1) if (Math.abs(a.elements[k]! - b.elements[k]!) > 1e-6) return false;
  return true;
}

/** Linear-blend each vertex (and normal) through its weighted per-bone matrices. */
function repose(geometry: BufferGeometry, perBone: readonly Matrix4[]): void {
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  const skinIndex = geometry.getAttribute('skinIndex');
  const skinWeight = geometry.getAttribute('skinWeight');
  const v = new Vector3();
  const n = new Vector3();
  const acc = new Vector3();
  const accN = new Vector3();
  const tmp = new Vector3();
  for (let i = 0; i < position.count; i += 1) {
    v.fromBufferAttribute(position, i);
    if (normal) n.fromBufferAttribute(normal, i);
    acc.set(0, 0, 0);
    accN.set(0, 0, 0);
    let total = 0;
    for (let k = 0; k < 4; k += 1) {
      const w = skinWeight.getComponent(i, k);
      if (w === 0) continue;
      const m = perBone[skinIndex.getComponent(i, k)]!;
      acc.addScaledVector(tmp.copy(v).applyMatrix4(m), w);
      if (normal) accN.addScaledVector(tmp.copy(n).transformDirection(m), w);
      total += w;
    }
    if (total === 0) continue; // an unweighted vertex rides nothing; it stays where it was bound
    position.setXYZ(i, acc.x / total, acc.y / total, acc.z / total);
    if (normal && accN.lengthSq() > 0) {
      accN.normalize();
      normal.setXYZ(i, accN.x, accN.y, accN.z);
    }
  }
  position.needsUpdate = true;
  if (normal) normal.needsUpdate = true;
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

// ── 2. units ───────────────────────────────────────────────────────────────

/**
 * Uniform scale about the origin, baked: every world (and local) matrix
 * conjugated by S keeps its rotation and scale and multiplies its translation
 * by s — so every node position, every vertex, every inverse bind and bind
 * matrix translation scales by s, whatever scales sit in the hierarchy.
 */
export function bakeUnitScale(scene: Object3D, s: number): void {
  if (s === 1) return;
  const geometries = new Set<BufferGeometry>();
  const binds = new Set<Matrix4>();
  scene.traverse((o) => {
    o.position.multiplyScalar(s);
    if (o instanceof Mesh) geometries.add(o.geometry as BufferGeometry);
    if (o instanceof SkinnedMesh) {
      for (const m of o.skeleton.boneInverses) binds.add(m);
      binds.add(o.bindMatrix);
    }
  });
  for (const g of geometries) {
    g.getAttribute('position')?.applyMatrix4(new Matrix4().makeScale(s, s, s));
    for (const morph of g.morphAttributes.position ?? []) morph.applyMatrix4(new Matrix4().makeScale(s, s, s));
    g.computeBoundingBox();
    g.computeBoundingSphere();
  }
  for (const m of binds) {
    m.elements[12]! *= s;
    m.elements[13]! *= s;
    m.elements[14]! *= s;
  }
  scene.traverse((o) => {
    if (o instanceof SkinnedMesh) o.bindMatrixInverse.copy(o.bindMatrix).invert();
  });
  scene.updateMatrixWorld(true);
}

// ── 3. materials ───────────────────────────────────────────────────────────

/** Phong/Lambert → metal-free PBR; roughness from Phong shininess (Blinn–Beckmann). */
export function convertMaterials(scene: Object3D): number {
  const done = new Map<Material, Material>();
  const convertOne = (m: Material): Material => {
    if (!(m instanceof MeshPhongMaterial || m instanceof MeshLambertMaterial)) return m;
    const hit = done.get(m);
    if (hit) return hit;
    const shininess = m instanceof MeshPhongMaterial ? m.shininess : 0;
    const out = new MeshStandardMaterial({
      name: m.name,
      color: m.color,
      map: m.map,
      normalMap: m instanceof MeshPhongMaterial ? m.normalMap : null,
      emissive: m.emissive,
      emissiveMap: m.emissiveMap,
      emissiveIntensity: m.emissiveIntensity,
      alphaMap: m.alphaMap,
      aoMap: m.aoMap,
      transparent: m.transparent,
      opacity: m.opacity,
      alphaTest: m.alphaTest,
      side: m.side,
      vertexColors: m.vertexColors,
      metalness: 0,
      roughness: Math.min(1, Math.max(0.04, Math.sqrt(2 / (shininess + 2)))),
    });
    if (m instanceof MeshPhongMaterial) out.normalScale.copy(m.normalScale);
    done.set(m, out);
    return out;
  };
  scene.traverse((o) => {
    if (!(o instanceof Mesh)) return;
    o.material = Array.isArray(o.material) ? o.material.map(convertOne) : convertOne(o.material);
  });
  return done.size;
}

// ── 4. textures ────────────────────────────────────────────────────────────

const TEXTURE_SLOTS = ['map', 'normalMap', 'emissiveMap', 'alphaMap', 'aoMap'] as const;

function decoded(t: Texture): boolean {
  const img = t.image as { width?: number; height?: number } | null | undefined;
  return img != null && (img.width ?? 0) > 0 && (img.height ?? 0) > 0;
}

/** Null every texture whose image never decoded; return `material.slot` for each. */
export function dropUndecodedTextures(scene: Object3D): string[] {
  const dropped: string[] = [];
  const seen = new Set<Material>();
  scene.traverse((o) => {
    if (!(o instanceof Mesh)) return;
    for (const m of Array.isArray(o.material) ? o.material : [o.material]) {
      if (seen.has(m)) continue;
      seen.add(m);
      const slots = m as unknown as Record<string, Texture | null | undefined>;
      for (const slot of TEXTURE_SLOTS) {
        const t = slots[slot];
        if (t && !decoded(t)) {
          slots[slot] = null;
          dropped.push(`${m.name || '(unnamed)'}.${slot}`);
          m.needsUpdate = true;
        }
      }
    }
  });
  return dropped;
}

/** A manager that counts texture loads, so the conversion can wait for them (or give up, loudly, at a ceiling). */
function trackingManager(): { manager: LoadingManager; done: () => Promise<void> } {
  let started = 0;
  let finished = 0;
  let wake: (() => void) | null = null;
  const manager = new LoadingManager();
  manager.itemStart = ((start) => (url: string) => {
    started += 1;
    start.call(manager, url);
  })(manager.itemStart);
  manager.itemEnd = ((end) => (url: string) => {
    finished += 1;
    end.call(manager, url);
    if (finished >= started) wake?.();
  })(manager.itemEnd);
  const done = (): Promise<void> =>
    finished >= started
      ? Promise.resolve()
      : new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, TEXTURE_WAIT_MS); // undecoded maps are then dropped and reported
          wake = () => {
            clearTimeout(timer);
            resolve();
          };
        });
  return { manager, done };
}

/** A manager whose texture requests resolve to an empty Texture — the parse completes without decoding an image. */
function textureFreeManager(): LoadingManager {
  const manager = new LoadingManager();
  const blank = {
    path: '',
    setPath(path: string) {
      this.path = path;
      return this;
    },
    load: () => new Texture(),
  };
  manager.addHandler(/.*/, blank as never);
  return manager;
}
